import { skipToken } from '@apollo/client/react'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IcBaselineKeyboardArrowDown from '~icons/ic/baseline-keyboard-arrow-down'
import IcBaselineKeyboardArrowUp from '~icons/ic/baseline-keyboard-arrow-up'
import MaterialSymbolsChevronRightRounded from '~icons/material-symbols/chevron-right-rounded'
import ArtworkLikeButton from 'src/components/Artwork/ArtworkLikeButton'
import BaseArtworkItemPreviewCard from 'src/components/Artwork/BaseArtworkItemPreviewCard'
import { CommentCountButton } from 'src/components/ItemComment/CommentCountButton'
import { ExpandableTagList } from 'src/components/Tag/TagList'
import { UILink } from 'src/components/ui/Link'
import UserFollowButton from 'src/components/UserFollowButton'
import { ProfilePageTab } from 'src/components/UserProfile/constants'
import UserLink from 'src/components/UserWidget/UserLink'
import { useCommentsRedesignExperiment } from 'src/experiments/experiments/2605/commentsRedesign'
import { usePagedListArtworks } from 'src/services/apollo/artwork'
import { useMyInfo } from 'src/services/apollo/auth'
import type { ArtworkDetailFragment } from 'src/services/apollo/generated/graphql'
import { useBlurNsfw } from 'src/services/helpers/preference'
import {
  getArtworkLink,
  getArtworkTitle,
  shouldArtworkBeBlurred,
} from 'src/utils/artwork'
import { cx } from 'src/utils/cx'
import { getNodesFromConnection } from 'src/utils/graphql'
import ArtworkDetailActions from '../ArtworkShare/ArtworkDetailShareActions'
import { ArtworkDetailContext } from '../common'
import styles from './index.module.css'
import { useNarrowScreenForArtworkDetail } from './utils'

export function ArtworkDetailTitle(
  props: {
    artwork: Pick<
      ArtworkDetailFragment,
      | 'id'
      | 'liked'
      | 'likedCount'
      | 'commentCount'
      | 'title'
      | 'prompts'
      | 'authorId'
      | 'tacks'
      | 'media'
      | 'mediaId'
      | 'videoMediaId'
      | 'type'
      | 'extra'
      | 'flag'
    >
    isSinglePage?: boolean
  } & StyleProps,
) {
  const { artwork, isSinglePage, className } = props
  const { id, liked, likedCount, commentCount } = artwork
  const { onLikeClickForRec, trackingArgs } = useContext(ArtworkDetailContext)
  const { isTreatment } = useCommentsRedesignExperiment()

  return (
    <section className={cx('w-full flex flex-col gap-2', className)}>
      <div
        className={cx(
          'flex justify-between items-start flex-col',
          isSinglePage ? 'lg:flex-row' : 'mmd:flex-row',
        )}
      >
        <h1
          className={'font-bold text-2xl flex-1 min-w-0 max-w-full break-words'}
          title={getArtworkTitle(artwork)}
        >
          {getArtworkTitle(artwork)}
        </h1>
        <div className="flex gap-2 items-center">
          <ArtworkDetailActions
            artwork={artwork}
            trackingArgs={{
              scene: 'artwork-detail',
              modal: isSinglePage ? undefined : true,
            }}
          />
          {isTreatment && <CommentCountButton count={commentCount ?? 0} />}
          <ArtworkLikeButton
            artworkId={id}
            artwork={artwork}
            initialCount={likedCount}
            initialValue={liked}
            countStyle={{
              color: 'inherit',
            }}
            afterClick={onLikeClickForRec}
            // Overwrite scene provided by ArtworkList
            trackingArgs={{ ...trackingArgs, scene: 'artwork-detail' }}
            oauthReturnOpensDetail={!isSinglePage}
          />
        </div>
      </div>
    </section>
  )
}

export function ArtworkDetailAuthorInfo(props: {
  artwork: ArtworkDetailFragment
}) {
  const { artwork } = props
  const { author } = artwork
  const { onClose } = useContext(ArtworkDetailContext)
  const { t } = useTranslation(['artwork'])
  const artworkListLink = `/@${author?.username}/${ProfilePageTab.artworks}`
  const { data: userInfo } = useMyInfo()

  return (
    <div className="flex gap-2 items-center">
      {author && (
        <>
          <div
            className="min-w-0"
            onClick={e => {
              e.stopPropagation()
              onClose?.()
            }}
          >
            <UserLink size={32} user={author} scene="image" />
          </div>
          {userInfo && userInfo.id !== author.id ? (
            <UserFollowButton
              user={author}
              userId={author.id}
              stateOverrides={{
                not_following: {
                  startIcon: null,
                  className: '!rounded-full font-medium text-xs min-w-0 !px-2',
                },
                follow_back: {
                  startIcon: null,
                  className: '!rounded-full font-medium text-xs min-w-0 !px-2',
                },
                following: {
                  startIcon: null,
                  className: '!rounded-full font-base text-xs min-w-0 !px-2',
                },
                mutual: {
                  startIcon: null,
                  className: '!rounded-full font-base text-xs min-w-0 !px-2',
                },
              }}
              className="shrink-0"
              scene="artwork"
            />
          ) : null}
          <UILink
            href={artworkListLink}
            target="_blank"
            endIcon={<MaterialSymbolsChevronRightRounded className="size-6" />}
            className={cx(
              'flex ml-auto text-sm relative items-center justify-center hover:bg-transparent whitespace-nowrap',
            )}
          >
            {t('author-artwork.more')}
          </UILink>
        </>
      )}
    </div>
  )
}

const PC_ARTWORKS_SHOWN = 5
const MOBILE_ARTWORKS_SHOWN = 3

export function ArtworkDetailAuthorArtworks(props: {
  artwork: ArtworkDetailFragment
  isSinglePage?: boolean
}) {
  const { artwork, isSinglePage } = props
  const { author } = artwork
  const { data, loading } = usePagedListArtworks(
    author?.id
      ? {
          authorId: author?.id,
          last: PC_ARTWORKS_SHOWN,
        }
      : skipToken,
  )
  const narrowScreen = useNarrowScreenForArtworkDetail(isSinglePage)
  const artworks = getNodesFromConnection(data)
    .reverse()
    .slice(0, narrowScreen ? MOBILE_ARTWORKS_SHOWN : PC_ARTWORKS_SHOWN)
  const [blurNsfw] = useBlurNsfw()

  if (!author) {
    return null
  }

  return (
    <div className="w-full">
      <div
        className={cx(
          'w-full relative grid gap-2',
          narrowScreen ? 'grid-cols-3' : 'grid-cols-5',
        )}
      >
        {artworks.map(artwork => {
          const blurForPreview = shouldArtworkBeBlurred(artwork) && blurNsfw
          const cardLabel = getArtworkTitle(artwork)
          return (
            <BaseArtworkItemPreviewCard
              key={artwork.id}
              mediaId={
                artwork?.mediaId ||
                artwork?.manga?.coverMediaId ||
                artwork?.manga?.chapter?.coverMediaId
              }
              media={artwork.media}
              nsfwBlur={blurForPreview}
              showExplicitIcon={blurForPreview}
              artwork={artwork}
              label={
                <span className="truncate" title={cardLabel}>
                  {cardLabel}
                </span>
              }
              contentClassName="no-underline"
              border="plain"
              hideShare
              showInfo={false}
              showLikeBtn
              likeBtn={
                <ArtworkLikeButton
                  artworkId={artwork.id}
                  initialCount={artwork.likedCount}
                  initialValue={artwork.liked}
                  hideValueOnZero
                />
              }
              to={getArtworkLink(artwork)}
              target="_blank"
            />
          )
        })}
        {loading &&
          Array.from({
            length: narrowScreen ? MOBILE_ARTWORKS_SHOWN : PC_ARTWORKS_SHOWN,
          }).map((_, index) => (
            <BaseArtworkItemPreviewCard key={index} loading showInfo={false} />
          ))}
      </div>
    </div>
  )
}

export function ArtworkDescriptionAndTags(props: {
  artwork: ArtworkDetailFragment
}) {
  const { extra, tacks } = props.artwork
  const artworkDescription = extra?.description

  const tacksAsTags = useMemo(() => {
    if (!tacks || tacks.length === 0) return []
    return tacks.map(tack => ({
      id: tack.id,
      name: tack.codeName,
      displayName: tack.displayName ?? tack.defaultName,
    }))
  }, [tacks])

  return (
    <>
      {artworkDescription && (
        <ArtworkDescription description={artworkDescription} />
      )}
      {tacksAsTags.length > 0 && (
        <ExpandableTagList type="artwork" tags={tacksAsTags} />
      )}
    </>
  )
}

function ArtworkDescription(props: { description: string }) {
  const { description } = props
  const descRef = useRef<HTMLDivElement>(null)
  const [expand, setExpand] = useState<boolean>(false)
  const [isLongDesc, setIsLongDesc] = useState(false)
  const { t } = useTranslation(['artwork'])

  useEffect(() => {
    if (!description) return
    if (!descRef.current) return
    setIsLongDesc(descRef.current.scrollHeight > descRef.current.clientHeight)
  }, [description])

  return (
    <div className="relative rounded-lg flex flex-col items-start gap-1 w-full">
      <p
        ref={descRef}
        className={cx(
          expand
            ? 'table table-fixed break-words' // break-words with unsepcified width needs display: table, which is incompatible with line-clamp
            : 'line-clamp-2 break-all',
          'w-full',
          'text-sm',
          'text-left',
          'whitespace-pre-line',
        )}
      >
        {description}
      </p>
      {isLongDesc && !expand && (
        <button
          className={cx(
            styles['show-more-horizontal-gradient'],
            'grid grid-rows-[1fr_auto] grid-cols-[auto] justify-center absolute right-0 bottom-0',
          )}
          onClick={() => setExpand(prev => !prev)}
        >
          <span className="row-span-full col-start-1 pl-2 pr-4 flex items-center">
            {expand ? (
              <IcBaselineKeyboardArrowUp className="size-5" />
            ) : (
              <IcBaselineKeyboardArrowDown className="size-5" />
            )}
            <span className="text-xs opacity-75">
              {expand ? t('artwork:show.less') : t('artwork:show.more')}
            </span>
          </span>
        </button>
      )}
    </div>
  )
}
